import { portalText } from "../i18n/portal.js";
/**
 * The gateway's `fetch`, made scope-aware (plan-014 A4, ED-12, design §27.6.4).
 *
 * Every request the generated client makes goes through here. Three things happen:
 *
 *   1. **Send.** A request carries `X-WeTalk-Scope-Generation` with the generation this tab is on
 *      (`./generation.ts`). The server ignores it where it does not check (keys, public routes, the
 *      identity entry routes) and refuses a stale one on every scoped route.
 *   2. **Refuse locally while stale.** Once the scope moved elsewhere and before this tab re-read its
 *      auth_session, a scoped request is not sent at all: it would only be refused, and a request that
 *      raced the refusal must never be answered from the new scope.
 *   3. **Drop a late answer.** A response to a request sent under one generation that arrives after the
 *      tab moved to another is DISCARDED ({@link StaleScopeResponseError}), never handed to the page:
 *      a delayed answer from the previous organization can never populate the new one (AC12-02).
 *
 * A `409 scope_changed` refusal tells the store the scope moved (with the generation the answer's
 * header names) and is then returned unchanged, so the caller's own error handling reads the API's
 * words; the scope guard does the clearing.
 *
 * The identity entry routes — the ones that READ or CHANGE the scope (`GET /v1/session`, the switch,
 * sign-in, sign-up, sign-out, creating an organization, invitations, the impersonation hand-off) — are
 * exempt from 2 and 3: they are how a tab learns its new generation. They are named by operationId and
 * their paths come from the generated operation table, so a path that moves in the spec moves here.
 */
import { API_OPERATION } from "@wetalk/api-client";
import { SCOPE_GENERATION_HEADER } from "@wetalk/domain";

import { isScopeGeneration, type ScopeGeneration } from "./generation.js";

/** The operations that read or change the scope itself, and therefore never carry a stale-scope check. */
export const SCOPE_ENTRY_OPERATION = [
  "getCurrentAuthSession",
  "switchAuthSessionAccount",
  "logInPerson",
  "logOutPerson",
  "signUpPerson",
  "discoverIdentity",
  "createAccount",
  "inspectInvitation",
  "acceptInvitation",
  // plan-014 B2's organization invitation link (INT, 2026-10-06): reading it shows who is looking; accepting it adds
  // memberships and the console switches into the organization — both identity entry routes, never scope-checked.
  "inspectAccountInvitation",
  "acceptAccountInvitation",
  "redeemImpersonationHandoff",
] as const;

/** A request was not sent because this tab's scope moved elsewhere; the page is about to be closed. */
export class ScopeChangedLocallyError extends Error {
  override readonly name = "ScopeChangedLocallyError";
  constructor() {
    super(
      portalText("shell", "you_switched_organization_or_workspace_in_another_tab_this_5d0c2c0a"),
    );
  }
}

/** An answer arrived for a scope this tab has left. It is discarded, never shown. */
export class StaleScopeResponseError extends Error {
  override readonly name = "StaleScopeResponseError";
  readonly sentUnder: number | null;
  readonly currentGeneration: number | null;
  constructor(sentUnder: number | null, currentGeneration: number | null) {
    super(portalText("shell", "that_answer_belongs_to_the_organization_or_workspace_this_81a8e822"));
    this.sentUnder = sentUnder;
    this.currentGeneration = currentGeneration;
  }
}

interface EntryRoute {
  readonly method: string;
  readonly path: string;
}

/** The entry routes' method and path, from the generated table. A name the spec lacks is a build fault. */
function entryRouteOf(operationId: (typeof SCOPE_ENTRY_OPERATION)[number]): EntryRoute {
  const found = API_OPERATION.find((one) => one.operationId === operationId);
  if (found === undefined) {
    throw new Error(`"${operationId}" is not an operation of the assembled spec; the scope entry list is out of date.`);
  }
  return { method: found.method.toUpperCase(), path: found.path };
}

const ENTRY_ROUTE: readonly EntryRoute[] = SCOPE_ENTRY_OPERATION.map(entryRouteOf);

/** Whether a request is one of the identity entry routes (method and exact path, query ignored). */
export function isScopeEntryRequest(method: string, url: string, baseUrl: string): boolean {
  let path: string;
  try {
    path = new URL(url, baseUrl).pathname;
  } catch {
    return false;
  }
  const upper = method.toUpperCase();
  return ENTRY_ROUTE.some((one) => one.method === upper && one.path === path);
}

function methodOf(input: RequestInfo | URL, init: RequestInit | undefined): string {
  if (init?.method !== undefined) return init.method;
  if (typeof input === "object" && "method" in input) return input.method;
  return "GET";
}

function urlOf(input: RequestInfo | URL): string {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.toString();
  return input.url;
}

/** The generation a response's header names, or `null` when it names none (or nothing readable). */
export function generationHeaderOf(response: Response): number | null {
  const raw = response.headers.get(SCOPE_GENERATION_HEADER);
  if (raw === null) return null;
  const value = Number(raw.trim());
  return isScopeGeneration(value) ? value : null;
}

/** Whether a 409 is the scope refusal, read from a clone so the caller still gets the whole body. */
async function isScopeChangedRefusal(response: Response): Promise<boolean> {
  if (response.status !== 409) return false;
  try {
    const body: unknown = await response.clone().json();
    if (typeof body !== "object" || body === null) return false;
    const error = (body as Record<string, unknown>)["error"];
    return typeof error === "object" && error !== null && (error as Record<string, unknown>)["code"] === "scope_changed";
  } catch {
    // A 409 whose body is not the envelope is not the scope refusal; the caller reads it as it is.
    return false;
  }
}

/**
 * `headers` with one more, in the shape it came in: the generated client passes a plain record, and
 * keeping it a record (rather than a `Headers` object) leaves every other header exactly as it was.
 */
function withHeader(headers: HeadersInit | undefined, name: string, value: string): HeadersInit {
  if (headers === undefined) return { [name]: value };
  if (headers instanceof Headers) {
    const copy = new Headers(headers);
    copy.set(name, value);
    return copy;
  }
  if (Array.isArray(headers)) return [...headers.filter(([key]) => key.toLowerCase() !== name), [name, value]];
  return { ...headers, [name]: value };
}

export interface ScopedFetchSettings {
  readonly fetch: typeof globalThis.fetch;
  readonly scope: ScopeGeneration;
  readonly baseUrl: string;
  /** False for a bearer-key client, which has no ambient credential and no tabs to keep apart. */
  readonly sendsGeneration: boolean;
}

export function scopedFetch(settings: ScopedFetchSettings): typeof globalThis.fetch {
  const { scope } = settings;
  return async (input, init) => {
    if (!settings.sendsGeneration) return settings.fetch(input, init);
    const entry = isScopeEntryRequest(methodOf(input, init), urlOf(input), settings.baseUrl);
    if (!entry && scope.isStale()) throw new ScopeChangedLocallyError();

    const sentUnder = scope.current();
    const response = await settings.fetch(
      input,
      sentUnder === null ? init : { ...init, headers: withHeader(init?.headers, SCOPE_GENERATION_HEADER, String(sentUnder)) },
    );
    if (entry) return response;

    if (await isScopeChangedRefusal(response)) {
      scope.noteLeft(generationHeaderOf(response));
      return response;
    }
    if (scope.current() !== sentUnder || scope.isStale()) {
      throw new StaleScopeResponseError(sentUnder, scope.current());
    }
    return response;
  };
}
